import React, { useState, useEffect, useRef } from 'react';
import { animate, stagger } from 'animejs';
import supabase from '../supabase/supabase';
import { formatPHP, formatDate } from '../utils/format';
import { startOfWeek, subWeeks, format } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTrendingDown, FiTrendingUp, FiDollarSign, FiShoppingBag } = FiIcons;
const TABLE_WEEKLY = 'weekly_balance_1739880000000';
const TABLE_EXPENSES = 'expenses_1739880000000';
const TABLE_TOPUPS = 'topups_1739880000000';

function useCountUp(target, duration = 1200) {
  const [display, setDisplay] = useState(0);
  const prev = useRef(0);
  useEffect(() => {
    const obj = { val: prev.current };
    animate(obj, {
      val: target, duration, ease: 'outExpo',
      onUpdate: () => setDisplay(obj.val),
    });
    prev.current = target;
  }, [target]);
  return display;
}

const Dashboard = () => {
  const [stats, setStats] = useState({ starting: 0, spent: 0, remaining: 0 });
  const [loading, setLoading] = useState(true);
  const [recentExpenses, setRecentExpenses] = useState([]);
  const cardsRef = useRef(null);
  const listRef = useRef(null);

  const startingDisplay = useCountUp(stats.starting);
  const spentDisplay = useCountUp(stats.spent);
  const remainingDisplay = useCountUp(stats.remaining);

  useEffect(() => { fetchDashboardData(); }, []);

  useEffect(() => {
    if (!loading && cardsRef.current) {
      animate(cardsRef.current.querySelectorAll('.stat-card'), {
        opacity: [0, 1], translateY: [30, 0],
        delay: stagger(100), duration: 500, ease: 'outExpo',
      });
    }
  }, [loading]);

  useEffect(() => {
    if (recentExpenses.length > 0 && listRef.current) {
      animate(listRef.current.querySelectorAll('.expense-row'), {
        opacity: [0, 1], translateX: [-20, 0],
        delay: stagger(60), duration: 400, ease: 'outExpo',
      });
    }
  }, [recentExpenses]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const today = new Date();
      const currentWeekStartStr = format(startOfWeek(today, { weekStartsOn: 0 }), 'yyyy-MM-dd');

      let { data: currentBalance, error: bError } = await supabase
        .from(TABLE_WEEKLY).select('*').eq('week_start', currentWeekStartStr).single();

      if (bError && bError.code === 'PGRST116') {
        const lastWeekStartStr = format(subWeeks(startOfWeek(today, { weekStartsOn: 0 }), 1), 'yyyy-MM-dd');
        const { data: lastBalance } = await supabase
          .from(TABLE_WEEKLY).select('*').eq('week_start', lastWeekStartStr).single();
        const carried_forward = (lastBalance && lastBalance.remaining > 0) ? Number(lastBalance.remaining) : 0;
        const { data: inserted, error: insertError } = await supabase
          .from(TABLE_WEEKLY).insert([{ week_start: currentWeekStartStr, starting_balance: carried_forward, spent: 0, remaining: carried_forward, carried_forward }]).select().single();
        if (insertError) throw insertError;
        currentBalance = inserted;
      } else if (bError) throw bError;

      const { data: expenses } = await supabase.from(TABLE_EXPENSES)
        .select('amount,supplier,purchase_date')
        .gte('purchase_date', currentWeekStartStr)
        .order('purchase_date', { ascending: false });
      const { data: topups } = await supabase.from(TABLE_TOPUPS)
        .select('amount').gte('date', currentWeekStartStr);

      const totalExpenses = expenses?.reduce((sum, e) => sum + Number(e.amount), 0) || 0;
      const totalTopups = topups?.reduce((sum, t) => sum + Number(t.amount), 0) || 0;
      const liveStarting = Number(currentBalance.carried_forward) + totalTopups;
      const liveRemaining = liveStarting - totalExpenses;

      await supabase.from(TABLE_WEEKLY).update({
        starting_balance: liveStarting, spent: totalExpenses, remaining: liveRemaining
      }).eq('id', currentBalance.id);

      setStats({ starting: liveStarting, spent: totalExpenses, remaining: liveRemaining });

      // Fetch last 5 expenses ever, independent of week filter
      const { data: recentData } = await supabase.from(TABLE_EXPENSES)
        .select('amount,supplier,purchase_date')
        .order('purchase_date', { ascending: false })
        .order('created', { ascending: false })
        .limit(5);
      setRecentExpenses(recentData || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
    </div>
  );

  const isLowBalance = stats.remaining < (stats.starting * 0.2);
  const spentPct = stats.starting > 0 ? Math.min((stats.spent / stats.starting) * 100, 100) : 0;

  return (
    <div className="space-y-5 pb-6">
      <header>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-400 text-sm mt-0.5">This week's overview</p>
      </header>

      {/* Stat Cards — 1 col mobile, 3 col desktop */}
      <div ref={cardsRef} className="grid grid-cols-1 sm:grid-cols-3 gap-3">

        {/* Starting Balance */}
        <div className="stat-card opacity-0 bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center space-x-4">
          <div className="w-11 h-11 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiDollarSign} className="text-lg" />
          </div>
          <div>
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Starting</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5">{formatPHP(startingDisplay)}</p>
          </div>
        </div>

        {/* Spent */}
        <div className="stat-card opacity-0 bg-white rounded-2xl border border-gray-100 shadow-sm p-4 flex items-center space-x-4">
          <div className="w-11 h-11 bg-red-50 text-red-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <SafeIcon icon={FiTrendingDown} className="text-lg" />
          </div>
          <div className="flex-1">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Spent</p>
            <p className="text-xl font-bold text-gray-900 mt-0.5">{formatPHP(spentDisplay)}</p>
            {/* Spend progress bar */}
            <div className="mt-2 h-1.5 bg-gray-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-red-400 rounded-full transition-all duration-1000"
                style={{ width: `${spentPct}%` }}
              />
            </div>
          </div>
        </div>

        {/* Remaining */}
        <div className={`stat-card opacity-0 rounded-2xl border shadow-sm p-4 flex items-center space-x-4 ${isLowBalance ? 'bg-orange-50 border-orange-100' : 'bg-emerald-50 border-emerald-100'}`}>
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0 ${isLowBalance ? 'bg-orange-100 text-orange-600' : 'bg-emerald-100 text-emerald-600'}`}>
            <SafeIcon icon={FiTrendingUp} className="text-lg" />
          </div>
          <div>
            <p className={`text-xs font-medium uppercase tracking-wide ${isLowBalance ? 'text-orange-500' : 'text-emerald-600'}`}>
              {isLowBalance ? '⚠ Low Balance' : 'Remaining'}
            </p>
            <p className={`text-xl font-bold mt-0.5 ${isLowBalance ? 'text-orange-700' : 'text-emerald-700'}`}>
              {formatPHP(remainingDisplay)}
            </p>
          </div>
        </div>
      </div>

      {/* Recent Expenses */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="px-4 py-3.5 md:px-6 md:py-4 border-b border-gray-100 flex justify-between items-center">
          <h2 className="text-sm md:text-base font-bold text-gray-900">Recent Expenses</h2>
          <span className="text-xs text-gray-400">This week</span>
        </div>

        {recentExpenses.length === 0 ? (
          <div className="py-10 text-center text-gray-400">
            <SafeIcon icon={FiShoppingBag} className="text-2xl mx-auto mb-2 opacity-30" />
            <p className="text-sm">No expenses this week</p>
          </div>
        ) : (
          <div ref={listRef} className="divide-y divide-gray-50">
            {recentExpenses.map((expense, idx) => (
              <div key={idx} className="expense-row opacity-0 px-4 py-3.5 md:px-6 flex justify-between items-center hover:bg-gray-50 transition-colors">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiShoppingBag} className="text-gray-400 text-xs" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900 text-sm">{expense.supplier}</p>
                    <p className="text-xs text-gray-400">{formatDate(expense.purchase_date)}</p>
                  </div>
                </div>
                <p className="font-bold text-gray-900 text-sm">{formatPHP(expense.amount)}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
